import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { AlertIcon, CheckCircleIcon } from './Icons';
import './AppDialog.css';

const AppDialogContext = createContext(null);

const DEFAULT_CONFIRM = {
  mode: 'confirm',
  title: 'Confirm action',
  message: '',
  note: '',
  details: [],
  confirmLabel: 'Confirm',
  cancelLabel: 'Cancel',
  variant: 'primary'
};

const DEFAULT_PROMPT = {
  ...DEFAULT_CONFIRM,
  mode: 'prompt',
  title: 'Input required',
  confirmLabel: 'Save',
  inputLabel: '',
  inputPlaceholder: '',
  defaultValue: ''
};

const getCancelValue = (mode) => (mode === 'prompt' ? null : false);

const AppDialog = ({ dialog, inputValue, setInputValue, onCancel, onConfirm }) => {
  const isPrompt = dialog.mode === 'prompt';
  const variant = dialog.variant === 'danger' ? 'danger' : 'primary';
  const details = Array.isArray(dialog.details) ? dialog.details : [];
  const Icon = variant === 'danger' ? AlertIcon : CheckCircleIcon;

  return (
    <div className="app-dialog-overlay" onMouseDown={onCancel}>
      <section
        className={`app-dialog app-dialog-${variant}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-dialog-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="app-dialog-icon" aria-hidden="true">
          <Icon size={20} />
        </div>

        <div className="app-dialog-content">
          <h2 id="app-dialog-title" className="app-dialog-title">{dialog.title}</h2>
          {dialog.message && <p className="app-dialog-message">{dialog.message}</p>}

          {details.length > 0 && (
            <dl className="app-dialog-details">
              {details.map((detail, index) => (
                <div className="app-dialog-detail-row" key={`${detail.label}-${index}`}>
                  <dt>{detail.label}</dt>
                  <dd>{detail.value === null || detail.value === undefined || detail.value === '' ? '-' : detail.value}</dd>
                </div>
              ))}
            </dl>
          )}

          {dialog.note && <p className="app-dialog-note">{dialog.note}</p>}

          {isPrompt && (
            <label className="app-dialog-field">
              {dialog.inputLabel && <span>{dialog.inputLabel}</span>}
              <textarea
                className="app-dialog-textarea"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                placeholder={dialog.inputPlaceholder}
                rows={4}
                autoFocus
              />
            </label>
          )}

          <div className="app-dialog-actions">
            <button type="button" className="btn app-dialog-cancel-btn" onClick={onCancel}>
              {dialog.cancelLabel}
            </button>
            <button
              type="button"
              className={`btn app-dialog-confirm-btn app-dialog-${variant}-btn`}
              onClick={onConfirm}
              autoFocus={!isPrompt}
            >
              {dialog.confirmLabel}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export const AppDialogProvider = ({ children }) => {
  const [dialog, setDialog] = useState(null);
  const [inputValue, setInputValue] = useState('');
  const resolverRef = useRef(null);
  const modeRef = useRef(DEFAULT_CONFIRM.mode);

  const closeDialog = useCallback((value) => {
    if (resolverRef.current) {
      resolverRef.current(value);
    }

    resolverRef.current = null;
    setDialog(null);
    setInputValue('');
  }, []);

  const openDialog = useCallback((options) => new Promise((resolve) => {
    if (resolverRef.current) {
      resolverRef.current(getCancelValue(modeRef.current));
    }

    modeRef.current = options.mode;
    resolverRef.current = resolve;
    setInputValue(options.defaultValue || '');
    setDialog(options);
  }), []);

  const confirm = useCallback((options = {}) => (
    openDialog({ ...DEFAULT_CONFIRM, ...options, mode: 'confirm' })
  ), [openDialog]);

  const prompt = useCallback((options = {}) => (
    openDialog({ ...DEFAULT_PROMPT, ...options, mode: 'prompt' })
  ), [openDialog]);

  useEffect(() => () => {
    if (resolverRef.current) {
      resolverRef.current(getCancelValue(modeRef.current));
    }
  }, []);

  useEffect(() => {
    if (!dialog) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeDialog(getCancelValue(dialog.mode));
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [closeDialog, dialog]);

  const value = useMemo(() => ({ confirm, prompt }), [confirm, prompt]);

  return (
    <AppDialogContext.Provider value={value}>
      {children}
      {dialog && (
        <AppDialog
          dialog={dialog}
          inputValue={inputValue}
          setInputValue={setInputValue}
          onCancel={() => closeDialog(getCancelValue(dialog.mode))}
          onConfirm={() => closeDialog(dialog.mode === 'prompt' ? inputValue : true)}
        />
      )}
    </AppDialogContext.Provider>
  );
};

export const useAppDialog = () => {
  const dialog = useContext(AppDialogContext);
  if (!dialog) {
    throw new Error('useAppDialog must be used inside AppDialogProvider');
  }

  return dialog;
};
